<template lang="pug">
    .stepper.purchase-form
        el-steps(:active="step" align-center)
            el-step(
                :title="purchaserName ? purchaserName : $t('Customer')"
                :description="billingAddress"
                @click.native="goToStep(1)"
                class="clickable"
            )

            //- :description="form.items.length ? form.items.length.toString() : undefined"
            el-step(
                :title="form.items.length ? $t('{number} Articles', { number: form.items.reduce((acc, item) => acc += parseInt(item.qty), 0) }) : $t('Articles')"
                :description="subTotal ? moneyFormat(subTotal, this.currency) : undefined"
                @click.native="goToStep(2)"
                :class="{ clickable: validUntilStep >= 2 }"
            )
            el-step(
                :title="shippingTabTitle"
                :description="shippingAddress"
                @click.native="goToStep(3)"
                :class="{ clickable: validUntilStep >= 3 }"
            )
            el-step(
                :title="form.paymentMethod ? form.paymentMethod.name : $t('Payment')"
                :description="grandTotal ? moneyFormat(grandTotal, this.currency) : undefined"
                @click.native="goToStep(4)"
                :class="{ clickable: validUntilStep >= 4 }"
            )
        .wrapper
            el-form(label-position="top" :model="form" :rules="rules" :validate-on-rule-change="false" ref="form")
                el-card(shadow="always" v-loading="loading")
                    .steps(v-if="! loading")
                        transition(name="slide-decent-vertical" mode="out-in")
                            CustomerStep(v-if="step === 1" :form="form" :rules="rules")
                            ArticlesStep(v-else-if="step === 2" :form="form" :rules="rules")
                            ShippingStep(v-else-if="step === 3" :form="form" :rules="rules" :sub-total="subTotal")
                            PaymentStep(v-else-if="step === 4" :form="form" :rules="rules" :grand-total="grandTotal")
                .buttons
                    el-button(
                        round v-if="! loading"
                        :disabled="step <= 1"
                        @click="prev"
                    ) {{ $t('Previous') }}
                    el-button(
                        round v-if="! loading && step < 4"
                        type="success"
                        @click="next"
                    ) {{ $t('Next') }}
                    span(v-else-if="! loading && step == 4")
                        el-button(
                            round
                            type="primary"
                            icon="el-icon-fa-check fas"
                            :loading="saving"
                            @click="submit($event, 'close')"
                        ) {{ $t('Save and close') }}
                        el-button(
                            round
                            type="success"
                            icon="el-icon-fa-save fas"
                            :loading="saving"
                            @click="submit($event, 'new')"
                        ) {{ $t('Save and new') }}
</template>

<i18n>
{
    "en": {
        "{number} Articles": "{number} Articles"
    },
    "de": {
        "Purchase could not be saved.": "Bestellung konnte nicht gespeichert werden.",
        "Purchase saved successfully!": "Bestellung erfolgreich gespeichert!",
        "Customer": "Kunde",
        "Articles": "Artikel",
        "{number} Articles": "{number} Artikel",
        "Shipping": "Versand",
        "Payment": "Zahlung",
        "Previous": "Zurück",
        "Next": "Weiter"
    }
}
</i18n>

<script>
import uniqid from 'uniqid';
import { mapActions, mapGetters } from 'vuex';

import store from '@/store';
import router from '@/router';
import { moneyFormat } from '@/filters';
import _ from 'lodash';

import CustomerStep from './Steps/Customer';
import ArticlesStep from './Steps/Articles';
import ShippingStep from './Steps/Shipping';
import PaymentStep from './Steps/Payment';

export default {
    name: 'NewPurchaseStepper',
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        member: {
            type: Object,
            default: null,
        },
    },
    data() {
        // const options = store.getters['ext_shop_purchase/options'] || []
        //
        // let tax_rate = null
        // let tax = _.get(store.getters['activeCms'], 'shop.defaultTax')
        //
        // if (options.tax.items.filter(el => el.default === true && el.reduced === false)) {
        //     tax_rate = 'regular'
        //     tax = null
        // }

        return {
            step: 1,
            validUntilStep: 1,
            saving: false,
            listRoute: { name: 'purchase-list' },
            form: {
                purchaser: {
                    id: undefined, // member id: undefined -> guest
                    // create: false,
                    gender: undefined,
                    first_name: '',
                    last_name: '',
                    mobile: '',
                    phone: '',
                    email: '',
                    // password: '',
                    billingAddress: undefined,
                    shippingAddress: undefined,
                },

                items: [],
                coupons: [],

                shippingMethod: undefined,
                paymentMethod: undefined,

                should_be_delivered_at: undefined,
                commentary: '',
            },
        };
    },
    components: {
        CustomerStep,
        ArticlesStep,
        ShippingStep,
        PaymentStep,
    },
    computed: {
        purchaserName() {
            return `${this.form.purchaser.first_name} ${this.form.purchaser.last_name}`.trim();
        },
        billingAddress() {
            if (!this.form.purchaser.billingAddress) return undefined;

            const street = this.form.purchaser.billingAddress.street || '';
            const zipcode = this.form.purchaser.billingAddress.zipcode || '';
            const city = this.form.purchaser.billingAddress.city || '';

            return _.trim(`${street}, ${zipcode} ${city}`, ', ');
        },
        shippingAddress() {
            if (!this.form.purchaser.shippingAddress) return undefined;

            const street = this.form.purchaser.shippingAddress.street || '';
            const zipcode = this.form.purchaser.shippingAddress.zipcode || '';
            const city = this.form.purchaser.shippingAddress.city || '';
            return _.trim(`${street}, ${zipcode} ${city}`, ', ');
        },
        rules() {
            let rules = {
                purchaser: {
                    gender: 'required',
                    first_name: 'required',
                    last_name: 'required',
                    email: 'required|email',
                    billingAddress: {
                        street: 'required',
                        zipcode: 'required',
                        city: 'required',
                        country: 'required',
                    },
                    shippingAddress: {
                        street: 'required',
                        zipcode: 'required',
                        city: 'required',
                        country: 'required',
                    }
                },
                items: 'required|min:1',
                shippingMethod: 'required',
                paymentMethod: {
                    name: 'required'
                },
            };

            if (this.form.purchaser.create) {
                rules.purchaser.customer = {
                    nr: 'unique_customer_nr',
                }
            }

            return rules
        },
        activeShop() {
            return this.activeCms.shop;
        },
        customerGroup() {
            const customer = this.form.purchaser.customer
            return _.get(customer, 'customerGroup') || _.get(this.activeCms, 'shop.defaultCustomerGroup')
        },
        currency() {
            return _.get(this.customerGroup, 'currency.iso4217') || 'EUR'
        },
        subTotal() {
            if (!this.form.items.length) return null;

            return this.form.items.reduce((acc, item) => {
                const price = item.sale_price || item.price || 0;
                return acc + (price * item.qty);
            }, 0);
        },
        grandTotal() {
            if (!this.form.items.length) return null;
            let total = this.subTotal;
            if (this.form.shippingMethod && this.form.shippingMethod.price) {
                total += this.form.shippingMethod.price;
            }
            return total;
        },
        shippingTabTitle() {
            if (this.form.shippingMethod) {
                let title = this.form.shippingMethod.name;
                if (this.form.shippingMethod.price) {
                    title += ` ${moneyFormat(this.form.shippingMethod.price, this.currency)}`;
                }
                return title;
            } else {
                return this.$t('Shipping')
            }
        },
        ...mapGetters({
            user: 'auth/user',

            // form: 'ext_shop_purchase/form',
            // rules: 'ext_shop_purchase/rules',

            options: 'ext_shop_purchase/options',

            // cms
            activeCms: 'activeCms',
            // enabledExtensions: 'enabledExtensions',
        }),
    },
    methods: {
        moneyFormat: moneyFormat,
        next() {
            // eslint-disable-next-line
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    ++this.step;
                    this.validUntilStep = this.step;
                } else {
                    this.validUntilStep = this.step - 1;
                }
            });
        },
        prev() {
            --this.step;
        },
        goToStep(step) {
            if (step <= this.validUntilStep) {
                this.step = step;
            } else {
                // this.$refs.form.validate((valid, errors) => {
                //     if (valid) {
                //         this.validUntilStep = this.step;
                //
                //         if (step <= this.validUntilStep) {
                //             this.step = step;
                //         }
                //     } else {
                //         this.validUntilStep = this.step - 1;
                //     }
                // });
            }
        },
        submit(event, action = 'new') {
            // eslint-disable-next-line
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    this.saving = true;

                    const purchase = _.cloneDeep(this.form)
                    purchase.sold_by = `${this.user.first_name} ${this.user.last_name}`.trim()

                    if (purchase.purchaser.id != null) {
                        delete purchase.purchaser.id;
                    }

                    if (purchase.shippingMethod.price) {
                        purchase.shippingMethod.custom_price = purchase.shippingMethod.price;
                        delete purchase.shippingMethod.price;
                    }

                    purchase.items.forEach(item => {
                        item.article = { id: item.article.id }
                        item.propertyValueOptions = item.propertyValueOptions.filter(el => el.id != null)
                    })

                    this.create(purchase)
                        .then(() => {
                            this.resetForm();

                            this.$notify.success({
                                title: this.$t('Purchase saved successfully!'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            });

                            switch (action) {
                                case 'new':
                                    this.step = 1;
                                    break;

                                case 'close':
                                    router.push(this.listRoute);
                                    this.step = 1;
                                    break;
                            }
                        })
                        .catch((err) => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Purchase could not be saved.'),
                                iconClass: 'fas el-icon-fa-times',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            });
                        })
                        .finally(() => {
                            this.saving = false;
                        });
                }
            });
        },
        resetForm() {
            this.form = {
                purchaser: {
                    id: undefined, // member id: undefined -> guest
                    // create: false,
                    gender: undefined,
                    first_name: '',
                    last_name: '',
                    mobile: '',
                    phone: '',
                    email: '',
                    // password: '',
                    billingAddress: undefined,
                    shippingAddress: undefined,
                },

                items: [],
                coupons: [],

                shippingMethod: undefined,
                paymentMethod: undefined,

                should_be_delivered_at: undefined,
                commentary: '',
            };

            this.$refs.form.resetFields()
        },
        ...mapActions({
            create: 'ext_shop_purchase/create',
        }),
    },
    mounted() {
        const form = store.getters['ext_shop_purchase/form']
        if (form.purchaser) this.form.purchaser = _.cloneDeep(form.purchaser)
    },
};
</script>

<style lang="sass" scoped>
.stepper.purchase-form
    max-width: 1200px
    margin: 0 auto
.wrapper
    margin: 0 6%
    .el-card
        margin: 15px 0
        min-height: 135px
    .buttons
        display: flex
        justify-content: space-between

.el-step.clickable
    cursor: pointer
    pointer-events: auto
    &:hover,
    &:focus
    ::v-deep
        .el-step__head,
        .el-step__title,
        .el-step__description
            color: $--shop-color-primary
            border-color: $--shop-color-primary
            font-weight: normal
.el-step
    pointer-events: none
    ::v-deep
        .el-step__title
            font-weight: normal
</style>
